/*
 * The single writer for an inventory attribute on an outgoing save payload.
 *
 * ⚠ Why this exists. Eight components carried their own local `setAttribute` closure - EditInventory,
 * ArrivalCaptureDetail, InventoryLocatedCaptureDetail, BookDealCapture, LocateDealCapture,
 * SoldInventoryCapture, ApprovePpaAction and InventoryConsignmentAgreementAction - already drifted
 * into three variants, with no test anywhere exercising the branch that lost data.
 *
 * ⚠ The defect they all shared. The delete guard was `value === ''` STRICT, and for the fields that
 * matter it could never fire: every screen builds selectedMakeModel with
 * `customMake === '' ? null : customMake`, so Make and Model arrive as NULL, skip the guard, and land
 * on `existingAttribute.value = value || ''` - writing an EMPTY STRING over a stored value. Saving
 * inventory 1082081 blanked its Make (Ferrari) and Model (296) exactly this way. It was silent
 * because Inventory.GetAttributeValueOrDefault skips blank rows, so a blanked attribute reads back as
 * "Unspecified" - which is also why clearing a custom make appeared to work at all.
 *
 * ⚠ Blank now DELETES rather than blanks, and the blank set is deliberately IDENTICAL to the set the
 * old code collapsed to '' - null, undefined, '', 0 and NaN. Keeping that set identical is what makes
 * this one behaviour change rather than two. It is also the right answer for both callers of a null:
 * a record resolved to a catalogue row has no business carrying a redundant Make attribute, and a
 * user who cleared a custom make wants it gone. Those two are indistinguishable upstream, because the
 * coercion above turns '' into null before anything here can tell them apart.
 */

// Exactly the values `value || ''` used to collapse into an empty string.
const isBlank = (value) => !value

const rowsNamed = (model, name) =>
  (model && model.attributes) ? model.attributes.filter((x) => x.name === name) : []

/*
 * ⚠ Mirrors Inventory.IsMultiValuedAttribute (Inventory.cs:3047), and the two lists MUST stay in step.
 * DamageNotes is a vehicle's damage HISTORY - there is no Damage entity and no Damage table anywhere,
 * so N rows named DamageNotes are the history, and Carfax appends one row per event. The server
 * therefore exempts it from name-matching; a client that name-matches it writes to a different row
 * than the server would, and on an unordered bag that row is arbitrary.
 */
const MULTI_VALUED_ATTRIBUTES = ['DamageNotes']

export const isMultiValuedAttribute = (name) => MULTI_VALUED_ATTRIBUTES.indexOf(name) >= 0

/**
 * The one row a screen owns, for an attribute that may have many.
 *
 * ⚠ NEWEST, not the first in the array. ServiceRequestService.cs:242 and both damage handlers all
 * read OrderByDescending(CreatedAt).FirstOrDefault, while every client copy took a bare `find` over a
 * collection mapped AsBag() with no ordering and no OrderBy in the DTO - so the screen and the shop
 * floor could disagree about which note is "the" note, and the client's pick was undefined.
 *
 * Blank rows are skipped, matching Inventory.GetAttributeValueOrDefault (Inventory.cs:2573). That
 * matters here: 21,262 of the 21,524 live DamageNotes rows are blank residue.
 */
export const readOwnedInventoryAttribute = (model, name) => {
  const populated = rowsNamed(model, name).filter((x) => !isBlank(x.value))
  if (populated.length === 0) {
    return {id: null, value: ''}
  }

  const newest = populated.slice().sort(
    (left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0)
  )[0]

  return {id: newest.id || null, value: newest.value}
}

/**
 * Every non-blank row, for read-only display. Exactly the filter the three agreement screens already
 * inline for their "Damage History" table.
 */
export const readInventoryAttributeHistory = (model, name) =>
  rowsNamed(model, name).filter((x) => !isBlank(x.value))

/**
 * Writes ONE row, identified by id, and NEVER by name.
 *
 * ⚠ This is the only safe writer for a multi-valued attribute. Owning nothing and being handed a
 * blank writes nothing at all - which is the case that made ApprovePpaAction destructive: it passed
 * an undefined value it had no field for, and the name-match deleted an arbitrary damage event on
 * every PPA approval.
 */
export const setInventoryAttributeRow = (model, name, attributeId, value) => {
  const ownedAttribute = attributeId
    ? rowsNamed(model, name).find((x) => x.id === attributeId)
    : null

  if (ownedAttribute) {
    if (isBlank(value)) {
      ownedAttribute.status = 'Deleted'
      return
    }
    ownedAttribute.value = value
    // Re-activated for the same reason as below: clear-then-retype must not save as Deleted.
    ownedAttribute.status = 'Active'
    return
  }

  /*
   * ⚠ An id that no longer resolves is treated as owning nothing, deliberately. Falling back to a
   * name-match here would reintroduce the arbitrary-row write this function exists to remove.
   */
  if (isBlank(value)) {
    return
  }

  if (!model.attributes) {
    model.attributes = []
  }

  model.attributes.push({name: name, value: value})
}

export const setInventoryAttribute = (model, name, value) => {
  /*
   * ⚠ Refused rather than handled. Name-matching a multi-valued attribute IS the defect - it picks an
   * arbitrary row out of N and overwrites or deletes it. Callers that own a specific row must use
   * setInventoryAttributeRow; pinned by the wiring spec, which fails if any component passes one of
   * these names here.
   */
  if (isMultiValuedAttribute(name)) {
    return
  }

  // Guarded: the three watcher components are handed a model that may carry no attributes collection.
  const existingAttribute = model.attributes ? model.attributes.find((x) => x.name === name) : null

  if (isBlank(value)) {
    if (existingAttribute) {
      existingAttribute.status = 'Deleted'
    }
    return
  }

  if (existingAttribute) {
    existingAttribute.value = value
    /*
     * ⚠ Required by the delete rule above, not a tidy-up. The watcher components run this on every
     * selector change, so "change the model (trim -> null -> Deleted), then pick a trim" is a real
     * sequence - and without re-activating, the row would save as Deleted and the new trim be lost.
     */
    existingAttribute.status = 'Active'
    return
  }

  if (!model.attributes) {
    model.attributes = []
  }

  model.attributes.push({name: name, value: value})
}
